/**
 * Application Constants
 *
 * Centralized constants for the frontend application.
 * Organized by domain: storage, polling, timeouts, external URLs.
 */

// ============================================================================
// Storage Keys
// ============================================================================

/**
 * Application name prefix for localStorage keys.
 * Used to namespace all storage keys to prevent conflicts.
 */
export const APP_STORAGE_PREFIX = 'tricorder' as const;

/**
 * Storage key for config store (language preferences).
 */
export const STORAGE_KEY_CONFIG = `${APP_STORAGE_PREFIX}-config` as const;

/**
 * Storage key for auth store (user and access token).
 */
export const STORAGE_KEY_AUTH = `${APP_STORAGE_PREFIX}-auth` as const;

/**
 * Storage key for the Home workspace navigation store (scope + selected patient).
 */
export const STORAGE_KEY_HOME_NAV = `${APP_STORAGE_PREFIX}-home-nav` as const;

/**
 * Storage key for response cache (offline Q&A pairs).
 */
export const STORAGE_KEY_RESPONSE_CACHE = `${APP_STORAGE_PREFIX}-response-cache` as const;

/**
 * Storage key for pending async chat turns. Persisted so a
 * reload/background-resume can poll GET /turns/{id} for an in-flight turn whose
 * live turn.* events were missed.
 */
export const STORAGE_KEY_PENDING_TURNS = `${APP_STORAGE_PREFIX}-pending-turns` as const;

// ============================================================================
// Polling Intervals (milliseconds)
// ============================================================================

/**
 * Polling interval for bookmark enrichment processing status.
 * Check every 3 seconds for enrichment completion.
 */
export const POLL_INTERVAL_BOOKMARK_ENRICHMENT = 3_000 as const;

/**
 * Polling interval for the publish-request approvals inbox (documents page).
 * Requests are created server-side by OTHER users (an owner asking to widen a
 * document's audience), so the approver's inbox has no local mutation to
 * invalidate it — a background poll keeps it current while the page is open.
 * 30s is a deliberately relaxed cadence; an approval decision is not a
 * bedside-latency operation.
 */
export const POLL_INTERVAL_PUBLISH_REQUESTS = 30_000 as const;

/**
 * Cap for the Home-header approvals badge count; anything above renders as
 * "<max>+". One named place to tighten if a wall-visible display should show
 * less detail.
 */
export const APPROVAL_BADGE_MAX = 99 as const;

/**
 * Default catch-up poll interval for in-flight async chat turns
 *. The live SSE channel is the primary delivery; this
 * interval is the backstop that reconciles a turn whose terminal event was
 * missed (reload drops the SSE replay cursor; backgrounded PWA drops the
 * socket). Each tick is a cheap store read that only fetches when this session
 * has a pending turn, and the loop self-terminates once the backend reports a
 * terminal state — there is no hard timeout (per the resilience rules).
 */
const DEFAULT_POLL_INTERVAL_PENDING_TURN = 4_000;

/**
 * Operator-tunable pending-turn poll interval (ms). Read from the deploy-time
 * env var VITE_POLL_INTERVAL_PENDING_TURN (same convention as
 * VITE_BULK_DOCUMENT_CONCURRENCY) so a customer can pace catch-up polling to
 * their backend. Falls back to the default when unset or not a positive
 * integer; floored at 1000ms so a misconfiguration can never hammer the API.
 */
export const POLL_INTERVAL_PENDING_TURN = ((): number => {
  const raw = import.meta.env.VITE_POLL_INTERVAL_PENDING_TURN;
  if (!raw) return DEFAULT_POLL_INTERVAL_PENDING_TURN;
  const n = Number(raw);
  if (!Number.isInteger(n) || n < 1_000) return DEFAULT_POLL_INTERVAL_PENDING_TURN;
  return n;
})();

// ============================================================================
// Stale Time (milliseconds)
// ============================================================================

/**
 * Stale time for query clusters (time/role/experience grouping).
 * Cache for 1 minute before considering stale.
 */
export const STALE_TIME_QUERY_CLUSTERS = 60_000 as const;

// ============================================================================
// Cache Limits
// ============================================================================
// External URLs
// ============================================================================

/**
 * Base URL for NHS SNOMED CT Browser.
 * Used to link SNOMED concepts to their full definition.
 */
export const SNOMED_BROWSER_BASE_URL = 'https://termbrowser.nhs.uk' as const;

// ============================================================================
// Cold-Start Retry Delays (milliseconds)
// ============================================================================

/**
 * Delay before retrying when Cloud Run returns empty 200 body during cold start.
 * Short delay (500ms) as the service is already accepting connections.
 */
export const COLD_START_EMPTY_BODY_DELAY_MS = 500 as const;

/**
 * Default delay before retrying 503 errors without Retry-After header.
 * Cloud Run cold-start default (5s) allows service initialization time.
 */
export const COLD_START_503_DEFAULT_DELAY_MS = 5_000 as const;

/**
 * Maximum allowed delay from Retry-After header on 503 errors.
 * Caps server-specified delays to prevent excessive wait times (10s max).
 */
export const COLD_START_503_MAX_DELAY_MS = 10_000 as const;

// ============================================================================
// Stream-connect network retry (transient connectivity, e.g. low-signal area)
// ============================================================================

/**
 * Number of automatic resends of the chat-send POST when the connection itself
 * fails transiently (a thrown fetch TypeError — DNS hiccup, dropped socket,
 * wifi/cell handoff). This is the low-network case: the request never reached
 * the server (or its response was lost), so a resend is the right response, not
 * an immediate error bubble. Safe to resend because the send carries a stable
 * Idempotency-Key — a duplicate POST maps to the SAME turn server-side, never a
 * second one (see api-service-go handleAsyncTurn). 0 disables auto-retry and
 * restores the old "surface the error, user taps Retry" behaviour.
 *
 * Distinct from the 503 cold-start retry (COLD_START_503_*): that is a server
 * telling us it is still starting; this is the network dropping under us.
 */
export const STREAM_CONNECT_NETWORK_RETRIES = 2 as const;

/**
 * Base backoff before the first network-error resend; doubles each attempt
 * (1s, 2s with the default retry count). Bounded and short — the user still
 * has the cancel button (the resilience rules say the user initiates abort,
 * never a hard timer), so this only smooths over a brief blip before either
 * succeeding or surfacing the error for a manual retry.
 */
export const STREAM_CONNECT_NETWORK_RETRY_BASE_DELAY_MS = 1_000 as const;

/**
 * Hard ceiling on a single network-retry backoff — half of
 * COLD_START_503_MAX_DELAY_MS (10s), deliberately shorter because a network
 * blip should surface faster than a full server cold-start. The exponential
 * term is capped at this BEFORE jitter, so raising
 * STREAM_CONNECT_NETWORK_RETRIES for a low-connectivity deployment can't let
 * the wait grow unbounded (4s, 8s, …).
 */
export const STREAM_CONNECT_NETWORK_RETRY_MAX_DELAY_MS = 5_000 as const;

/**
 * Jitter spread (fraction of the capped backoff) added before each resend.
 * Per the distributed-systems rules (capped exponential backoff + JITTER): a
 * ward of nurses whose shared WiFi drops together must NOT retry in lockstep,
 * or they synchronise into a spike on the backend. 0.2 = up to +20% random
 * skew, enough to de-synchronise without noticeably lengthening the wait.
 */
export const STREAM_CONNECT_NETWORK_RETRY_JITTER_RATIO = 0.2 as const;

/**
 * Brief settle pause before a user-tapped retry resends. Long enough to let a
 * flapping connection breathe, short enough not to feel stuck — and abort-aware
 * (see useStreamingChat.retry), so Cancel during it is honoured immediately.
 */
export const RETRY_PRE_SEND_DELAY_MS = 1_000 as const;

/**
 * Timeout for backend health check requests.
 * Long timeout (45s) accounts for Cloud Run cold starts with model loading.
 */
export const BACKEND_HEALTH_TIMEOUT_MS = 45_000 as const;

/**
 * Number of initial health-check attempts during which a failure — a 5xx from
 * the load balancer, or a thrown timeout/network error — is treated as a cold
 * start (surface a calm "starting", not the actionable "Server unavailable").
 * A genuine outage persists past this many attempts and then surfaces with the
 * retry action. At the 3s retry interval this is a ~6-9s grace.
 */
export const HEALTH_COLD_START_GRACE_ATTEMPTS = 3 as const;

// ============================================================================
// SSE / Real-time Events
// ============================================================================

/**
 * Threshold for consecutive `schema_version`-rejected SSE events before
 * surfacing a "client out of date" toast and logging at error level.
 *
 * Rationale: a one-off bad payload is logged per-event at warn. A sustained
 * stream of rejected events (>= this many in a row) means the backend has
 * bumped the contract and this client is effectively dead in the water —
 * the user must reload to get a build that matches the new schema.
 */
export const SSE_SCHEMA_MISMATCH_TOAST_THRESHOLD = 10 as const;

// ============================================================================
// Bulk Operations
// ============================================================================

/**
 * Default cap on simultaneous in-flight requests for a bulk document action
 * (bulk visibility-change / retry / delete). Caps the fan-out so a 50-row bulk
 * action doesn't open 50 sockets against a cold-starting backend; each item
 * still runs independently and one failure never aborts the rest.
 */
const DEFAULT_BULK_DOCUMENT_CONCURRENCY = 4;

/**
 * Operator-tunable bulk concurrency. Read from the deploy-time env var
 * VITE_BULK_DOCUMENT_CONCURRENCY (same convention as
 * VITE_PROMOTED_PROTOCOL_CTA_THRESHOLD) so a customer whose backend tolerates
 * more — or fewer — concurrent ingest re-triggers can tune it without a code
 * change. Falls back to the default when unset or not a positive integer;
 * floored at 1 so a misconfiguration can never stall the bulk worker pool.
 */
export const BULK_DOCUMENT_CONCURRENCY = ((): number => {
  const raw = import.meta.env.VITE_BULK_DOCUMENT_CONCURRENCY;
  if (!raw) return DEFAULT_BULK_DOCUMENT_CONCURRENCY;
  const n = Number(raw);
  if (!Number.isInteger(n) || n < 1) return DEFAULT_BULK_DOCUMENT_CONCURRENCY;
  return n;
})();

/**
 * Shortest query the /users directory endpoint will answer (see `searchUsers`
 * in api/endpoints/users.ts, which enforces it). Lives here rather than in that
 * module so a typeahead can honour the contract without importing the API
 * client — the import alone would drag the client into every test that renders
 * a search box.
 */
export const MIN_USER_SEARCH_QUERY_LENGTH = 2;

const DEFAULT_SEARCH_DEBOUNCE_MS = 300;

/**
 * How long a typeahead waits after the last keystroke before asking the server.
 * Read from VITE_SEARCH_DEBOUNCE_MS (same convention as the tunables above) so a
 * deployment on a slower link can pace it without a code change.
 *
 * One value for every search box in the app. It used to be two — 300ms in the
 * user combobox and 220ms in the protocol run catalog — which is drift, not a
 * decision: nobody chose to make protocol search feel different from user
 * search.
 */
export const SEARCH_DEBOUNCE_MS = ((): number => {
  const raw = import.meta.env.VITE_SEARCH_DEBOUNCE_MS;
  if (!raw) return DEFAULT_SEARCH_DEBOUNCE_MS;
  const n = Number(raw);
  if (!Number.isInteger(n) || n < 0) return DEFAULT_SEARCH_DEBOUNCE_MS;
  return n;
})();

const DEFAULT_DOCUMENT_PAGE_SIZE = 50;

/**
 * Document-list page size (per "Load more" fetch). Read from the deploy-time
 * env var VITE_DOCUMENT_PAGE_SIZE (same convention as BULK_DOCUMENT_CONCURRENCY)
 * so a deployment can pace pagination against its backend without a code change.
 * Falls back to the default when unset or not a positive integer; floored at 1
 * so a misconfiguration can never produce a zero-length page.
 */
export const DOCUMENT_PAGE_SIZE = ((): number => {
  const raw = import.meta.env.VITE_DOCUMENT_PAGE_SIZE;
  if (!raw) return DEFAULT_DOCUMENT_PAGE_SIZE;
  const n = Number(raw);
  if (!Number.isInteger(n) || n < 1) return DEFAULT_DOCUMENT_PAGE_SIZE;
  return n;
})();
